import * as React from 'react';
import { Box, Typography, Button, Paper, Chip, Stack } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PaymentMethod } from '../../models/Organization';
import { RoomBookingStatus, PassBookingState } from '../../models/RoomBooking';
import PassBookingChip from '../passes/PassBookingChip';

interface BookingConfirmationStepProps {
  paymentMethod?: PaymentMethod;
  paymentStatus?: 'pending' | 'completed' | 'failed';
  bookingStatus?: RoomBookingStatus;
  bookingId?: string;
  isRecurringBooking?: boolean;
  /** Tutor lesson booking: uses tutor routes and copy. Defaults to room booking. */
  flow?: 'room' | 'tutor';
  /** When `flow` is `tutor`, set true if the API confirmed the booking immediately. */
  tutorAutoConfirmed?: boolean;
  /** Present when the create response paid (fully or partially) with a pass — flat fields, no nested `pass` object yet. */
  passId?: string | null;
  passBookingState?: PassBookingState | null;
}

export default function BookingConfirmationStep({ 
  paymentMethod, 
  paymentStatus,
  bookingStatus,
  bookingId,
  isRecurringBooking = false,
  flow = 'room',
  tutorAutoConfirmed = false,
  passId,
  passBookingState,
}: BookingConfirmationStepProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isTutorFlow = flow === 'tutor';
  const isConfirmed = isTutorFlow
    ? Boolean(tutorAutoConfirmed)
    : bookingStatus === RoomBookingStatus.AUTO_CONFIRMED ||
      bookingStatus === RoomBookingStatus.AUTO_CONFIRMED_PENDING_PAYMENT ||
      bookingStatus === RoomBookingStatus.CONFIRMED_BY_HOST;

  const viewBookingPath = bookingId
    ? isTutorFlow
      ? `/user/tutor-bookings/${bookingId}`
      : `/user/bookings/${bookingId}`
    : '';
  const myBookingsPath = isTutorFlow ? '/user/tutor-bookings' : '/user/bookings';

  return (
    <Box sx={{ py: 3 }}>
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon 
          sx={{ 
            fontSize: 60, 
            color: 'success.main',
            mb: 2 
          }} 
        />
        <Typography variant="h5" gutterBottom>
          {isConfirmed
            ? t('bookingSteps.autoConfirmedTitle', 'Booking Confirmed')
            : t('bookingSteps.requestSent', 'Booking Request Sent')}
        </Typography>
        
        {/* Payment Status */}
        {paymentMethod === PaymentMethod.ONLINE && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
            <PaymentIcon color="primary" />
            <Chip
              label={
                paymentStatus === 'completed' 
                  ? t('payment.status.completed', 'Payment Completed')
                  : paymentStatus === 'failed'
                  ? t('payment.status.failed', 'Payment Failed')
                  : t('payment.status.pending', 'Payment Pending')
              }
              color={
                paymentStatus === 'completed' 
                  ? 'success' 
                  : paymentStatus === 'failed'
                  ? 'error'
                  : 'warning'
              }
              variant="outlined"
            />
          </Stack>
        )}

        {passId && passBookingState && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
            <PassBookingChip
              pass={{
                passId,
                passBookingState,
                passDetailUrl: `/passes/${passId}`,
              }}
            />
          </Stack>
        )}
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {isConfirmed
            ? isTutorFlow
              ? t(
                  'bookingSteps.tutorAutoConfirmedMessage',
                  'Your lesson booking was confirmed. We will send you a reminder before the session.'
                )
              : t('bookingSteps.autoConfirmedMessage', 'Your booking was confirmed instantly based on host payment policy.')
            : isTutorFlow
              ? t(
                  'bookingSteps.tutorWaitForApproval',
                  'Please wait for the tutor to approve your booking request. We will notify you once it is approved.'
                )
              : t('bookingSteps.waitForApproval', 'Please wait for the host to approve your booking request. We\'ll send you a notification once it\'s approved.')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          {bookingId && !isRecurringBooking && viewBookingPath && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate(viewBookingPath)}
              size="large"
            >
              {t('booking.viewBooking', 'View Booking')}
            </Button>
          )}
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate(myBookingsPath)}
            size="large"
          >
            {isTutorFlow
              ? t('navigation.tutorBookings', 'Go to Tutor Bookings')
              : t('navigation.myBookings', 'Go to My Bookings')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
} 